import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { StudentDocumentService } from '../../../../../../services/student-document.service';
import { GlobalConfig, ToastrService } from 'ngx-toastr';
import { AlertToast } from '../../../../../../shared/notifications/alert.toast';

@Component({
  selector: 'app-dni-section',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './dni-section.component.html',
  styleUrl: './dni-section.component.css'
})
export class DniSectionComponent implements OnInit, OnChanges {
  @Input() userId!: number;
  @Input() user: any;
  @Output() dniUpdated = new EventEmitter<void>();

  dniExpirationDate: string = '';
  dniFrontImage: any = null;
  dniBackImage: any = null;
  loadingFront: boolean = false;
  loadingBack: boolean = false;
  savingDate: boolean = false;
  options: GlobalConfig;

  constructor(
    private studentDocService: StudentDocumentService,
    private toastr: ToastrService
  ) {
    this.options = this.toastr.toastrConfig;
  }

  ngOnInit(): void {
    this.loadDniInfo();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['user'] && this.user) {
      this.loadDniInfo();
    }
  }

  loadDniInfo(): void {
    if (this.user) {
      // Extraer la fecha directamente del string sin conversión de zona horaria
      if (this.user.dni_expiration_date) {
        // Si viene en formato YYYY-MM-DD, usarlo directamente
        // Si viene con hora (YYYY-MM-DDTHH:mm:ss), extraer solo la parte de la fecha
        this.dniExpirationDate = this.user.dni_expiration_date.split('T')[0];
      } else {
        this.dniExpirationDate = '';
      }
      this.dniFrontImage = this.user.media?.find((m: any) => m.collection_name === 'dni_front');
      this.dniBackImage = this.user.media?.find((m: any) => m.collection_name === 'dni_back');
    }
  }

  getDniStatus(): 'valid' | 'expiring' | 'expired' | 'no-date' {
    if (!this.dniExpirationDate) return 'no-date';

    const expirationDate = new Date(this.dniExpirationDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysDiff = Math.floor((expirationDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (daysDiff < 0) return 'expired';
    if (daysDiff <= 30) return 'expiring';
    return 'valid';
  }

  getStatusColor(): string {
    const status = this.getDniStatus();
    const colors: Record<string, string> = {
      'valid': 'text-green-600 bg-green-100',
      'expiring': 'text-yellow-600 bg-yellow-100',
      'expired': 'text-red-600 bg-red-100',
      'no-date': 'text-gray-600 bg-gray-100'
    };
    return colors[status];
  }

  getStatusText(): string {
    const status = this.getDniStatus();
    const texts: Record<string, string> = {
      'valid': 'Valido',
      'expiring': 'Proximo a caducar',
      'expired': 'Caducado',
      'no-date': 'Sin fecha'
    };
    return texts[status];
  }

  getDaysUntilExpiration(): number | null {
    if (!this.dniExpirationDate) return null;
    const expirationDate = new Date(this.dniExpirationDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.floor((expirationDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  }

  saveDniExpiration(): void {
    if (!this.dniExpirationDate) {
      this.errorToast('Error', 'Debe introducir una fecha de expiracion');
      return;
    }

    this.savingDate = true;
    this.studentDocService.updateDniInfo(this.userId, {
      dni_expiration_date: this.dniExpirationDate
    }).subscribe({
      next: (response) => {
        this.successToast('Éxito', 'Fecha de expiracion actualizada correctamente');
        this.savingDate = false;
        this.dniUpdated.emit();
      },
      error: (error) => {
        console.error('Error al actualizar fecha DNI:', error);
        this.errorToast('Error', 'Error al actualizar la fecha');
        this.savingDate = false;
      }
    });
  }

  onFrontFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.uploadDniFrontImage(file);
    }
  }

  onBackFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.uploadDniBackImage(file);
    }
  }

  uploadDniFrontImage(file: File): void {
    this.loadingFront = true;
    this.studentDocService.uploadDniFrontImage(this.userId, file).subscribe({
      next: (response) => {
        this.successToast('Éxito', 'Parte delantera del DNI subida correctamente');
        this.dniFrontImage = response.data.media?.find((m: any) => m.collection_name === 'dni_front');
        this.loadingFront = false;
        this.dniUpdated.emit();
      },
      error: (error) => {
        console.error('Error al subir parte delantera DNI:', error);
        this.errorToast('Error', 'Error al subir la imagen');
        this.loadingFront = false;
      }
    });
  }

  uploadDniBackImage(file: File): void {
    this.loadingBack = true;
    this.studentDocService.uploadDniBackImage(this.userId, file).subscribe({
      next: (response) => {
        this.successToast('Éxito', 'Parte trasera del DNI subida correctamente');
        this.dniBackImage = response.data.media?.find((m: any) => m.collection_name === 'dni_back');
        this.loadingBack = false;
        this.dniUpdated.emit();
      },
      error: (error) => {
        console.error('Error al subir parte trasera DNI:', error);
        this.errorToast('Error', 'Error al subir la imagen');
        this.loadingBack = false;
      }
    });
  }

  getDniFrontImageUrl(): string {
    return this.dniFrontImage?.original_url || '';
  }

  getDniBackImageUrl(): string {
    return this.dniBackImage?.original_url || '';
  }

  isFrontPdf(): boolean {
    return this.dniFrontImage?.mime_type === 'application/pdf';
  }

  isBackPdf(): boolean {
    return this.dniBackImage?.mime_type === 'application/pdf';
  }

  successToast(title: string, msg: string): void {
    const opt: any = this.options;
    opt.toastComponent = AlertToast;
    opt.progressBar = true;
    opt.type = 'success';
    opt.toastClass = 'toast';
    this.toastr.show(msg, title, opt);
  }

  errorToast(title: string, msg: string): void {
    const opt: any = this.options;
    opt.toastComponent = AlertToast;
    opt.progressBar = true;
    opt.type = 'error';
    opt.toastClass = 'toast';
    this.toastr.show(msg, title, opt);
  }
}
